import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import authStorage from '../utils/authStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check authentication on mount
    useEffect(() => {
        const checkAuth = async () => {
            const token = authStorage.getToken();
            const cachedAdmin = authStorage.getCachedAdmin();

            if (token) {
                try {
                    // Start with cached data to show UI immediately
                    if (cachedAdmin) {
                        setAdmin(cachedAdmin);
                        setIsAuthenticated(true);
                    }

                    // Then fetch fresh data from server to update permissions/state
                    const freshAdmin = await authService.getProfile();
                    if (freshAdmin) {
                        setAdmin(freshAdmin);
                        setIsAuthenticated(true);
                        authStorage.updateCachedAdmin(freshAdmin);
                    }
                } catch (error) {
                    // Only logout if it's a 401 Unauthorized, not just a network error
                    if (error.response?.status === 401) {
                        console.error('Session expired, logging out');
                        logout();
                    }
                }
            }

            setLoading(false);
        };

        checkAuth();
    }, []);

    const login = async (email, password, rememberMe = false) => {
        try {
            const result = await authService.login(email, password, rememberMe);
            
            if (result.requires_2fa) {
                return { success: true, requires_2fa: true, temp_token: result.temp_token };
            }

            const { admin: adminData } = result;
            setAdmin(adminData);
            setIsAuthenticated(true);
            return { success: true, admin: adminData };
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Login failed';
            return { success: false, message };
        }
    };

    const verify2FA = async (token, temp_token) => {
        try {
            const { admin: adminData } = await authService.verify2FA(token, temp_token);
            setAdmin(adminData);
            setIsAuthenticated(true);
            return { success: true, admin: adminData };
        } catch (error) {
            const message = error.response?.data?.message || error.message || '2FA verification failed';
            return { success: false, message };
        }
    };

    const updateAdminData = (newData) => {
        const updatedAdmin = { ...admin, ...newData };
        setAdmin(updatedAdmin);
        authStorage.updateCachedAdmin(updatedAdmin);
    };

    const logout = async () => {
        await authService.logout();
        setAdmin(null);
        setIsAuthenticated(false);
    };

    const isSuperAdmin = () => {
        return admin?.role === 'super_admin';
    };

    const hasPermission = useCallback((module, action) => {
        if (!admin) return false;
        if (module === 'tickets' && ['create', 'comment'].includes(action)) return true;
        
        // Super Admin bypass
        if (admin.role === 'super_admin') return true;
        
        // If permissions is missing or null, default to false
        const permissions = admin.permissions || {};
        
        // Handle both object and potentially Map-like structures if they occur
        const modulePermissions = permissions[module] || {};
        
        return !!modulePermissions[action];
    }, [admin]);

    const getDefaultRoute = useCallback((targetAdmin = null) => {
        const currentAdmin = targetAdmin || admin;
        if (!currentAdmin) return '/login';
        
        // Super Admin bypass
        if (currentAdmin.role === 'super_admin' || (currentAdmin.permissions?.dashboard?.view)) return '/dashboard';
        
        const routes = [
            { module: 'merchants', path: '/merchants' },
            { module: 'merchant_search', path: '/merchant-search' },
            { module: 'tickets', path: '/tickets' },
            { module: 'finance_reconciliation', path: '/finance-reconciliation' },
            { module: 'users', path: '/users' },
            { module: 'master_providers', path: '/games/master-providers' },
            { module: 'payment_gateway_master_providers', path: '/payment-gateways/master-providers' },
            { module: 'countries', path: '/games/countries' },
            { module: 'currencies', path: '/games/currencies' },
            { module: 'categories', path: '/games/categories' },
            { module: 'sub_categories', path: '/games/sub-categories' },
            { module: 'sources', path: '/games/sources' },
            { module: 'providers', path: '/games/providers' },
            { module: 'payment_gateway_categories', path: '/payment-gateways/categories' },
            { module: 'payment_gateway_sources', path: '/payment-gateways/sources' },
            { module: 'payment_gateway_providers', path: '/payment-gateways/providers' },
            { module: 'audit_logs', path: '/logs' },
        ];

        const permissions = currentAdmin.permissions || {};
        for (const route of routes) {
            const modulePermissions = permissions[route.module] || {};
            if (modulePermissions.view) {
                return route.path;
            }
        }

        return '/profile'; // Fallback to profile if no other permissions
    }, [admin]);

    const getAuthHeader = () => {
        const token = authStorage.getToken();
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    };

    const value = {
        admin,
        isAuthenticated,
        loading,
        login,
        verify2FA,
        logout,
        isSuperAdmin,
        hasPermission,
        getDefaultRoute,
        getAuthHeader,
        updateAdminData
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
